/**
 * Bulk item parser for template sections.
 * Supports CSV, TSV, Pipe-delimited, and plain text item lists with intelligent type detection.
 */

import { ITEM_TYPES, type ItemType } from '@pius/contracts';
import { slugifyKey } from './bulkOptionParser';

export interface ParsedItem {
  key: string;
  label: string;
  item_type: ItemType;
  isValid: boolean;
}

export interface ParseItemsResult {
  items: ParsedItem[];
  delimiterDetected: 'csv' | 'tsv' | 'pipe' | 'plain';
  hasHeader: boolean;
}

const VALID_ITEM_TYPES = new Set<string>(ITEM_TYPES);

export function normalizeItemType(rawType: string, label: string): ItemType {
  const norm = rawType.toLowerCase().trim().replace(/[\s-]/g, '_');
  if (VALID_ITEM_TYPES.has(norm)) {
    return norm as ItemType;
  }
  if (
    norm === 'text' ||
    norm === 'text_field' ||
    norm === 'string' ||
    norm === 'note' ||
    norm === 'notes'
  ) {
    return 'free_text';
  }
  if (norm === 'boolean' || norm === 'boolean_field' || norm === 'bool' || norm === 'check') {
    return 'yes_no';
  }
  if (norm === 'number_field' || norm === 'int' || norm === 'integer' || norm === 'count') {
    return 'number';
  }
  if (
    norm === 'date' ||
    norm === 'time' ||
    norm === 'date_field' ||
    norm === 'time_field' ||
    norm === 'datetime'
  ) {
    return 'date_time';
  }
  if (norm === 'file' || norm === 'attachment' || norm === 'upload') {
    return 'attachment_ref';
  }
  return detectItemTypeFromLabel(label);
}

export function detectItemTypeFromLabel(label: string): ItemType {
  const lower = label.toLowerCase();
  if (
    lower.includes('reading') ||
    lower.includes('gospel') ||
    lower.includes('psalm') ||
    lower.includes('epistle') ||
    lower.includes('lectura') ||
    lower.includes('evangelio') ||
    lower.includes('salmo') ||
    lower.includes('scripture')
  ) {
    return 'scripture_ref';
  }

  if (
    lower.includes('music') ||
    lower.includes('hymn') ||
    lower.includes('song') ||
    lower.includes('chant') ||
    lower.includes('acclamation') ||
    lower.includes('música') ||
    lower.includes('canto') ||
    lower.includes('himno') ||
    lower.includes('aclamación')
  ) {
    return 'music_ref';
  }

  if (
    lower.includes('presider') ||
    lower.includes('priest') ||
    lower.includes('deacon') ||
    lower.includes('reader') ||
    lower.includes('cantor') ||
    lower.includes('celebrante') ||
    lower.includes('lector') ||
    lower.includes('person')
  ) {
    return 'person_ref';
  }

  return 'single_option';
}

export function parseItemImport(
  rawText: string,
  existingKeys: Set<string> = new Set(),
): ParseItemsResult {
  const trimmed = rawText.trim();
  if (!trimmed) {
    return { items: [], delimiterDetected: 'plain', hasHeader: false };
  }

  const lines = trimmed
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) {
    return { items: [], delimiterDetected: 'plain', hasHeader: false };
  }

  const firstLine = lines[0] ?? '';

  let delimiter: string | null = null;
  let delimiterName: 'csv' | 'tsv' | 'pipe' | 'plain' = 'plain';

  if (firstLine.includes('\t')) {
    delimiter = '\t';
    delimiterName = 'tsv';
  } else if (firstLine.includes('|')) {
    delimiter = '|';
    delimiterName = 'pipe';
  } else if (firstLine.includes(',')) {
    delimiter = ',';
    delimiterName = 'csv';
  }

  const parseLine = (line: string): string[] => {
    if (!delimiter) return [line];
    if (delimiter === '\t' || delimiter === '|') {
      return line.split(delimiter).map((c) => c.trim());
    }
    // CSV with quotes
    const pattern = /(?:^|,)(?:"([^"]*)"|([^",]*))/g;
    const cells: string[] = [];
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(line)) !== null) {
      cells.push((match[1] ?? match[2] ?? '').trim());
    }
    return cells;
  };

  const headerCells = parseLine(firstLine).map((c) => c.toLowerCase());
  const hasHeader =
    headerCells.includes('key') ||
    headerCells.includes('label') ||
    headerCells.includes('title') ||
    headerCells.includes('type') ||
    headerCells.includes('tipo') ||
    headerCells.includes('clave') ||
    headerCells.includes('nombre');

  const startIndex = hasHeader ? 1 : 0;
  const items: ParsedItem[] = [];
  const usedKeys = new Set<string>(existingKeys);

  const keyCol =
    headerCells.indexOf('key') !== -1 ? headerCells.indexOf('key') : headerCells.indexOf('clave');
  const labelCol =
    headerCells.indexOf('label') !== -1
      ? headerCells.indexOf('label')
      : headerCells.indexOf('title') !== -1
        ? headerCells.indexOf('title')
        : headerCells.indexOf('nombre');
  const typeCol =
    headerCells.indexOf('type') !== -1 ? headerCells.indexOf('type') : headerCells.indexOf('tipo');

  for (let i = startIndex; i < lines.length; i++) {
    const rawLine = lines[i];
    if (!rawLine) continue;

    const cells = parseLine(rawLine);
    let key = '';
    let label = '';
    let rawType = '';

    if (hasHeader && cells.length > 1) {
      if (keyCol !== -1 && cells[keyCol]) key = cells[keyCol]!;
      if (labelCol !== -1 && cells[labelCol]) label = cells[labelCol]!;
      if (typeCol !== -1 && cells[typeCol]) rawType = cells[typeCol]!;
    } else if (cells.length >= 3) {
      key = cells[0] ?? '';
      label = cells[1] ?? '';
      rawType = cells[2] ?? '';
    } else if (cells.length === 2) {
      key = cells[0] ?? '';
      label = cells[1] ?? '';
    } else if (cells.length === 1) {
      label = cells[0] ?? '';
    }

    if (!label && key) {
      label = key;
    }

    if (!key && label) {
      key = slugifyKey(label);
    } else {
      key = slugifyKey(key);
    }

    if (!key) continue;

    // Disambiguate duplicate keys
    let uniqueKey = key;
    let counter = 2;
    while (usedKeys.has(uniqueKey)) {
      uniqueKey = `${key}_${counter}`;
      counter++;
    }
    usedKeys.add(uniqueKey);

    const itemType: ItemType = normalizeItemType(rawType, label);

    items.push({
      key: uniqueKey,
      label: label || uniqueKey,
      item_type: itemType,
      isValid: Boolean(uniqueKey && label),
    });
  }

  return {
    items,
    delimiterDetected: delimiterName,
    hasHeader,
  };
}

export function generateItemsCsvSample(): string {
  return `key,label,type
first_reading,"First Reading",scripture_ref
responsorial_psalm,"Responsorial Psalm",scripture_ref
second_reading,"Second Reading",scripture_ref
gospel,"Gospel",scripture_ref
gathering_song,"Gathering Hymn",music_ref
communion_hymn,"Communion Hymn",music_ref
unity_candle,"Lighting of Unity Candle",yes_no
presider,"Celebrant / Presider",person_ref
`;
}
